import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, EditArticleButton, StatusDot } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { useArticles } from '@/hooks/useArticles';
import type { ArticleSummary } from '@/types/article';

import styles from './Twitter.module.css';
import { motivoLegivel } from '@/services/erroLegivel';

const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'horoscopo', label: 'Horóscopo (25)' },
  { value: 'esportes', label: 'Esportes (13)' },
  { value: 'tecnologia', label: 'Tecnologia (99)' },
] as const;

type FilterKey = (typeof FILTERS)[number]['value'];

const FILTER_CATEGORIES: Record<Exclude<FilterKey, 'all'>, number[]> = {
  horoscopo: [25],
  esportes: [13],
  tecnologia: [99],
};

function isoNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function Twitter() {
  const { t } = useTranslation(['articles', 'common']);
  const [filter, setFilter] = useState<FilterKey>('all');

  const filters: { value: FilterKey; label: string }[] = [
    { value: 'all', label: t('articles:twitter.filter_all') },
    { value: 'horoscopo', label: t('articles:twitter.filter_horoscope') },
    { value: 'esportes', label: t('articles:twitter.filter_sports') },
    { value: 'tecnologia', label: t('articles:twitter.filter_tech') },
  ];

  const params = useMemo(() => {
    const base = {
      after: isoNDaysAgo(7),
      per_page: 50,
      status: 'publish',
    } as const;
    if (filter === 'all') return base;
    return { ...base, categories: FILTER_CATEGORIES[filter] };
  }, [filter]);

  const { data, isLoading, error } = useArticles(params);

  const articles = data?.items ?? [];
  const withTweet = articles.filter((a) => a.twitter_post_id);
  const withoutTweet = articles.filter((a) => !a.twitter_post_id);

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>{t('articles:twitter.title')}</h1>
        <Badge tone="info" size="sm">
          {t('articles:twitter.badge_7days')}
        </Badge>
      </header>

      <div className={styles.notice}>
        {/* Aqui havia uma nota de desenvolvedor — nome de workflow do N8N (que
            saiu do produto) e o caminho de uma coluna do WordPress. Quem lê
            esta tela é quem cuida da cobertura, não de quem escreveu o schema:
            o texto agora diz o que fazer para o monitor encher. */}
        {/* Rótulo honesto: o cruzamento site×X não tem quem o faça. O campo que
            separa as duas listas (article.twitter_post_id) vem do META DO
            WORDPRESS; o caminho de publicação do sistema grava SocialPost e
            NÃO escreve esse meta de volta (social_engine/engine.py:91-106) —
            quem escrevia era o workflow do N8N que saiu do produto. Os três
            contadores abaixo são count() do array de artigos, não métrica de
            rede. */}
        <strong className={styles.noticeGap}>{t('articles:twitter.notice_gap')}</strong>
        <strong>{t('articles:twitter.notice_prefix')}</strong> {t('articles:twitter.notice_body_1')}{' '}
        {t('articles:twitter.notice_body_2')}
      </div>

      <div className={styles.filters} role="group">
        {filters.map((f) => (
          <button
            key={f.value}
            className={`${styles.filterBtn} ${filter === f.value ? styles.filterBtnActive : ''}`}
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{articles.length}</span>
          <span className={styles.metricLabel}>{t('articles:twitter.metric_posts')}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{withTweet.length}</span>
          <span className={styles.metricLabel}>{t('articles:twitter.metric_with_tweet')}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricNumber}>{withoutTweet.length}</span>
          <span className={styles.metricLabel}>{t('articles:twitter.metric_without_tweet')}</span>
        </div>
      </div>

      {isLoading && <p className={styles.muted}>{t('common:status.loading')}</p>}
      {error && <p className={styles.error}>{motivoLegivel(error)}</p>}

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionTitle}>
          {t('articles:twitter.section_with_tweet', { count: withTweet.length })}
        </h2>
        {withTweet.length === 0 && (
          <p className={styles.muted}>
            {t('articles:twitter.empty_with_tweet')}
          </p>
        )}
        <ul className={styles.list}>
          {withTweet.map((article) => (
            <TwitterRow key={article.id} article={article} hasTweet />
          ))}
        </ul>
      </section>

      <section className={styles.sectionBlock}>
        <h2 className={styles.sectionTitle}>
          {t('articles:twitter.section_without_tweet', {
            count: Math.min(withoutTweet.length, 10),
          })}
        </h2>
        <ul className={styles.list}>
          {withoutTweet.slice(0, 10).map((article) => (
            <TwitterRow key={article.id} article={article} hasTweet={false} />
          ))}
        </ul>
      </section>
    </section>
  );
}

interface RowProps {
  article: ArticleSummary;
  hasTweet: boolean;
}

function TwitterRow({ article, hasTweet }: RowProps) {
  const { t } = useTranslation(['articles', 'common']);
  return (
    <li className={styles.row}>
      <StatusDot state={hasTweet ? 'ok' : 'off'} />
      <div className={styles.rowBody}>
        <Link to={`/artigos/${article.id}?site=${article.site}`} className={styles.rowLink}>
          {article.title}
        </Link>
        <div className={styles.rowMeta}>
          <span>{formatarDataHora(article.published_at)}</span>
          {hasTweet && article.twitter_post_id && (
            <a
              href={`https://x.com/i/status/${article.twitter_post_id}`}
              target="_blank"
              rel="noreferrer"
              className={styles.tweetLink}
            >
              {t('articles:twitter.view_tweet')}
            </a>
          )}
        </div>
      </div>
      <EditArticleButton id={article.id} size="sm" variant="ghost" style={{ marginLeft: 'auto' }} />
    </li>
  );
}
